import "@/App.css";
import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { Layout } from "@/components/Layout";
import { ScrollToTop } from "@/components/ScrollToTop";
import { AdminAuthProvider } from "@/context/AdminAuthContext";
import { ProtectedAdminRoute } from "@/components/ProtectedAdminRoute";
import Home from "@/pages/Home";

const AllBanks = lazy(() => import("@/pages/AllBanks"));
const Compare = lazy(() => import("@/pages/Compare"));
const Calculator = lazy(() => import("@/pages/Calculator"));
const Rankings = lazy(() => import("@/pages/Rankings"));
const About = lazy(() => import("@/pages/About"));
const Partners = lazy(() => import("@/pages/Partners"));
const PrivateBanking = lazy(() => import("@/pages/PrivateBanking"));
const PrivateBankingCompare = lazy(() => import("@/pages/PrivateBankingCompare"));
const PaymentResult = lazy(() => import("@/pages/PaymentResult"));
const BankDetail = lazy(() => import("@/pages/BankDetail"));
const CountriesIndex = lazy(() => import("@/pages/CountriesIndex"));
const CountryPage = lazy(() => import("@/pages/CountryPage"));
const FeesIndex = lazy(() => import("@/pages/FeesIndex"));
const FeeCategoryPage = lazy(() => import("@/pages/FeeCategoryPage"));
const Contact = lazy(() => import("@/pages/Contact"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfUse = lazy(() => import("@/pages/TermsOfUse"));
const CookiePolicy = lazy(() => import("@/pages/CookiePolicy"));
const Disclaimer = lazy(() => import("@/pages/Disclaimer"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminInbox = lazy(() => import("@/pages/AdminInbox"));

const RouteFallback = () => (
  <div data-testid="route-loading-fallback" className="min-h-[60vh] flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-[#059669] border-t-transparent rounded-full animate-spin" />
  </div>
);

// Only /admin/* routes need the admin session check (GET /auth/me) — scoping the
// provider here avoids firing an authenticated request (and its 401) on every public page.
const AdminAuthLayout = () => (
  <AdminAuthProvider>
    <Outlet />
  </AdminAuthProvider>
);

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route element={<Layout />}>
                <Route index element={<Home />} />
                <Route path="/banks" element={<AllBanks />} />
                <Route path="/compare" element={<Compare />} />
                <Route path="/calculator" element={<Calculator />} />
                <Route path="/rankings" element={<Rankings />} />
                <Route path="/banks/:id" element={<BankDetail />} />
                <Route path="/countries" element={<CountriesIndex />} />
                <Route path="/countries/:slug" element={<CountryPage />} />
                <Route path="/fees" element={<FeesIndex />} />
                <Route path="/fees/:slug" element={<FeeCategoryPage />} />
                <Route path="/about" element={<About />} />
                <Route path="/partners" element={<Partners />} />
                <Route path="/private-banking" element={<PrivateBanking />} />
                <Route path="/private-banking/compare" element={<PrivateBankingCompare />} />
                <Route path="/payment/success" element={<PaymentResult outcome="success" />} />
                <Route path="/payment/cancel" element={<PaymentResult outcome="cancel" />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-use" element={<TermsOfUse />} />
                <Route path="/cookie-policy" element={<CookiePolicy />} />
                <Route path="/disclaimer" element={<Disclaimer />} />
              </Route>
              <Route element={<AdminAuthLayout />}>
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route element={<ProtectedAdminRoute />}>
                  <Route path="/admin/inbox" element={<AdminInbox />} />
                </Route>
              </Route>
            </Routes>
          </Suspense>
        </BrowserRouter>
      </HelmetProvider>
      <Toaster />
    </div>
  );
}

export default App;
